"use client"

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { SearchData, searchProductsClient } from "@/lib/data/search"
import { convertToLocale } from "@lib/util/money"
import { normalizeUnitType } from "@lib/util/normalize-unit"
import { resolveImageUrl } from "@lib/util/image-url"
import { cn } from "@lib/utils"
import { SearchIcon, X } from "lucide-react"
import Image from "next/image"
import Link from "next/link"
import React, { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

type Props = {
  fullWidth?: boolean
}

const SearchBox = ({ fullWidth = false }: Props) => {
  const { t, i18n } = useTranslation()
  const [query, setQuery] = useState("")
  const [results, setResults] = useState<SearchData[]>([])
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [showEmptyState, setShowEmptyState] = useState(false)
  const [enrichedResults, setEnrichedResults] = useState<Map<string, { calculated_price?: number; original_price?: number; currency_code?: string; unit_price_type_name?: string }>>(new Map())
  const wrapperRef = useRef<HTMLDivElement | null>(null)
  const debounceRef = useRef<NodeJS.Timeout | null>(null)

  const handleBlur: React.FocusEventHandler<HTMLDivElement> = (e) => {
    const next = e.relatedTarget as Node | null
    if (wrapperRef.current && next && wrapperRef.current.contains(next)) return
    setOpen(false)
    setShowEmptyState(false)
  }

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    if (query.trim().length < 3) {
      setResults([])
      if (query.trim().length === 0) {
        setOpen(false)
        setShowEmptyState(false)
      } else {
        setOpen(true)
        setShowEmptyState(true)
      }
      return
    }
    debounceRef.current = setTimeout(async () => {
      setLoading(true)
      const data = await searchProductsClient(query, 20)
      setResults(data || [])
      setOpen(true)
      setShowEmptyState(false)
      setLoading(false)
    }, 300)
  }, [query])

  const handleInputFocus = () => {
    if (query.trim().length < 3) {
      setOpen(true)
      setShowEmptyState(true)
    } else if (results.length > 0) {
      setOpen(true)
    }
  }

  const clearSearch = () => {
    setQuery("")
    setResults([])
    setOpen(false)
    setShowEmptyState(false)
  }

  const getNavigationUrl = (item: any) => {
    if (item.type === 'category') {
      return `/categories/${item.handle}`
    }
    return `/products/${item.handle}`
  }

  const getTypeLabel = (type: string) =>
    type === "category"
      ? t("search.resultTypeCategory")
      : t("search.resultTypeProduct")

  const calculateDiscount = (marketPrice: number, salesPrice: number) => {
    if (!marketPrice || !salesPrice || marketPrice <= salesPrice) return null
    const discount = Math.round(((marketPrice - salesPrice) / marketPrice) * 100)
    return discount > 0 ? discount : null
  }

  const formatPrice = (price: number) => {
    const locale = i18n.language?.startsWith("tr") ? "tr-TR" : "en-US"
    return new Intl.NumberFormat(locale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(price)
  }

  // Fetch calculated prices for products that don't have them from the search API
  useEffect(() => {
    const fetchCalculatedPrices = async () => {
      const productsNeedingPrice = results.filter(
        (item) => item.type === 'product' && !item.calculated_price && item.handle && !enrichedResults.has(item.handle)
      )

      if (productsNeedingPrice.length === 0) return

      // Limit to first 10 products to avoid too many API calls
      const productsToFetch = productsNeedingPrice.slice(0, 10)

      const pricePromises = productsToFetch.map(async (item) => {
        try {
          const BACKEND_URL = process.env.NEXT_PUBLIC_MEDUSA_BACKEND_URL
          const regionResponse = await fetch(`${BACKEND_URL}/store/store-regions`, {
            headers: {
              "x-publishable-api-key": process.env.NEXT_PUBLIC_MEDUSA_PUBLISHABLE_KEY || "",
            },
          })
          const regionData = await regionResponse.json()
          const regionId = regionData?.region?.[0]?.id

          if (!regionId) return null

          // Use proxy so the server attaches the customer's Authorization header,
          // ensuring price list overrides for their customer group are applied
          const productResponse = await fetch(
            `/api/store-proxy/products-by-handle?handle=${item.handle}&region_id=${regionId}`,
            {
              method: "GET",
              headers: { "Content-Type": "application/json" },
            }
          )

          if (!productResponse.ok) return null

          const { product } = await productResponse.json()
          const productData = product?.[0]

          if (!productData?.variants?.length) return null

          // Get the cheapest variant's calculated price
          const cheapestVariant = productData.variants
            .filter((v: any) => !!v.calculated_price)
            .sort((a: any, b: any) => {
              return (
                a.calculated_price.calculated_amount -
                b.calculated_price.calculated_amount
              )
            })[0]

          if (!cheapestVariant?.calculated_price) return null

          return {
            handle: item.handle,
            calculated_price: cheapestVariant.calculated_price.calculated_amount,
            original_price: cheapestVariant.calculated_price.original_amount,
            currency_code: cheapestVariant.calculated_price.currency_code || 'TRY',
            unit_price_type_name: productData.unit_price_type_name,
          }
        } catch (error) {
          console.error(`Error fetching calculated price for ${item.handle}:`, error)
          return null
        }
      })

      const priceData = await Promise.all(pricePromises)

      setEnrichedResults((prev) => {
        const newEnrichedResults = new Map(prev)
        priceData.forEach((data) => {
          if (data) {
            newEnrichedResults.set(data.handle, {
              calculated_price: data.calculated_price,
              original_price: data.original_price,
              currency_code: data.currency_code,
              unit_price_type_name: data.unit_price_type_name,
            })
          }
        })
        return newEnrichedResults
      })
    }

    if (results.length > 0) {
      fetchCalculatedPrices()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [results])

  return (
    <div
      className="flex flex-col w-full relative"
      ref={wrapperRef}
      onBlur={handleBlur}
    >
      <div className="relative flex flex-row w-full" role="search">
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className={cn(
            "w-full border-orange-200 bg-white rounded-l-full rounded-r-none border-r-0 focus-visible:ring-2 focus-visible:ring-orange-200 focus-visible:outline-none",
            !fullWidth && "md:w-[350px]"
          )}
          placeholder={t("search.placeholder")}
          onFocus={handleInputFocus}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              clearSearch()
                ; (e.target as HTMLInputElement).blur()
            }
          }}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls="search-results"
        />
        {query && (
          <button
            type="button"
            onClick={clearSearch}
            className="absolute right-16 top-1/2 -translate-y-1/2 text-slate-400 hover:text-orange-600"
            aria-label={t("search.clear")}
            tabIndex={0}
          >
            <X className="w-4 h-4" />
          </button>
        )}
        <Button
          className="h-full rounded-l-none rounded-r-full border border-orange-200 bg-gradient-to-r from-orange-400 via-orange-300 to-amber-300 py-2.5 text-white hover:from-orange-500 hover:via-orange-400 hover:to-amber-400"
          aria-label={t("common.search")}
        >
          <SearchIcon className="w-4 h-4" />
        </Button>
      </div>

      {open && (
        <div
          id="search-results"
          role="listbox"
          className="absolute top-full left-0 right-0 z-50 mt-3 rounded-2xl border border-orange-100 bg-white shadow-xl"
        >
          <div className="px-4 py-3 border-b border-orange-100">
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold text-slate-700">
                {t("search.resultsTitle")}
              </span>
              {query.trim().length > 0 && (
                <button
                  onClick={clearSearch}
                  className="text-xs text-slate-500 hover:text-orange-600 transition-colors"
                >
                  {t("search.clear")}
                </button>
              )}
            </div>
          </div>

          <div className="max-h-96 overflow-auto">
            {loading ? (
              <div className="px-4 py-8 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500 mx-auto mb-3"></div>
                <p className="text-sm text-slate-500">{t("search.searching")}</p>
              </div>
            ) : showEmptyState ? (
              <div className="px-4 py-8 text-center">
                <div className="w-16 h-16 bg-orange-50 rounded-full flex items-center justify-center mx-auto mb-4">
                  <SearchIcon className="w-8 h-8 text-orange-400" />
                </div>
                <h3 className="text-lg font-medium text-slate-900 mb-2">
                  {t("search.emptyStateTitle")}
                </h3>
                <p className="text-sm text-slate-500 mb-4">
                  {t("search.emptyStateHint")}
                </p>

              </div>
            ) : results.length === 0 ? (
              <div className="px-4 py-8 text-center">
                <div className="w-16 h-16 bg-orange-50 rounded-full flex items-center justify-center mx-auto mb-4">
                  <SearchIcon className="w-8 h-8 text-orange-400" />
                </div>
                <h3 className="text-lg font-medium text-slate-900 mb-2">
                  {t("search.noResults")}
                </h3>
                <p className="text-sm text-slate-500">
                  {t("search.noResultsForQuery", { query })}
                </p>
              </div>
            ) : (
              <div className="divide-y divide-orange-50">
                {results.map((item) => (
                  <div key={item.id} className="transition-colors hover:bg-orange-50/60">
                    <Link
                      href={getNavigationUrl(item)}
                      className="flex items-center gap-4 p-4"
                      onClick={() => {
                        setOpen(false)
                        setShowEmptyState(false)
                      }}
                    >
                      <div className="flex-shrink-0">
                        <div className="w-12 h-12 bg-slate-100 rounded-lg overflow-hidden ring-1 ring-orange-100 hover:ring-2 hover:ring-orange-300 transition-all">
                          <Image
                            src={resolveImageUrl(item.image) || "/images_public/101-YAPI.com.png"}
                            alt={item.name || item.handle}
                            width={48}
                            height={48}
                            className="h-full w-full object-cover"
                          />
                        </div>
                      </div>

                      <div className="flex-1 min-w-0">
                        <h4 className="text-sm font-medium text-slate-900 hover:text-orange-600 transition-colors truncate">
                          {item.name || item.handle}
                        </h4>
                        {item.type === 'product' ? (() => {
                          // Use calculated price if available (from API or enriched), otherwise fall back to sales_price
                          const enriched = enrichedResults.get(item.handle)
                          const calculatedPrice = item.calculated_price ?? enriched?.calculated_price
                          const originalPrice = item.original_price ?? enriched?.original_price
                          const currencyCode = item.currency_code ?? enriched?.currency_code ?? 'TRY'
                          const displayPrice = calculatedPrice ?? item.sales_price
                          const displayOriginalPrice = originalPrice ?? item.market_price

                          if (displayPrice != null && displayPrice === 0) {
                            return (
                              <p className="text-xs text-red-500 mt-1 font-medium">
                                {t("product.productBeingSupplied")}
                              </p>
                            )
                          }
                          if (displayPrice != null) {
                            const hasDiscount = displayOriginalPrice != null && displayOriginalPrice > displayPrice
                            const discountPercent = hasDiscount && displayOriginalPrice
                              ? calculateDiscount(displayOriginalPrice, displayPrice)
                              : null

                            return (
                              <div className="flex items-center gap-2 mt-1">
                                {hasDiscount && displayOriginalPrice != null && (
                                  <>
                                    <span className="text-xs text-gray-500 line-through">
                                      {calculatedPrice || item.calculated_price_formatted
                                        ? convertToLocale({ amount: displayOriginalPrice, currency_code: currencyCode })
                                        : `${formatPrice(displayOriginalPrice)} TL`}
                                    </span>
                                    {discountPercent && (
                                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300">
                                        -%{discountPercent}
                                      </span>
                                    )}
                                  </>
                                )}
                                <span className="text-sm font-semibold text-green-600 dark:text-green-400">
                                  {calculatedPrice || item.calculated_price_formatted
                                    ? convertToLocale({ amount: displayPrice, currency_code: currencyCode })
                                    : `${formatPrice(displayPrice)} TL`}
                                </span>
                              </div>
                            )
                          } else {
                            return (
                              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{t("search.priceUnavailable")}</p>
                            )
                          }
                        })() : null}
                      </div>

                      <div className="flex-shrink-0">
                        <span className={cn(
                          "inline-flex items-center px-3 py-1 rounded-full text-xs font-medium",
                          item.type === 'category'
                            ? "bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300"
                            : "bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300"
                        )}>
                          {(() => {
                            const unitPriceTypeName = item.unit_price_type_name ?? enrichedResults.get(item.handle)?.unit_price_type_name;
                            if (item.type === 'product' && unitPriceTypeName) {
                              return t(`cartDropdown.unitTypes.${normalizeUnitType(unitPriceTypeName)}` as any);
                            } else if (item.type === 'product') {
                              return t("product.quantity");
                            }
                            return getTypeLabel(item.type);
                          })()}
                        </span>
                      </div>
                    </Link>
                  </div>
                ))}
              </div>
            )}
          </div>

          {results.length > 0 && (
            <div className="px-4 py-3 bg-gray-50 dark:bg-gray-800/50 border-t border-gray-100 dark:border-gray-800 rounded-b-xl">
              <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                <span>{t("search.resultsCount", { count: results.length })}</span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default SearchBox
